import * as React from "react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { RootState } from "../../reducers/index";
import { useAppDispatch, useAppSelector } from "../../store/index";
import {
    useRegisterClientMutation,
} from "../../reducers/registerapi"; // adjust path as needed
import { RegisterClientRequest } from "../../interfaces/auth";
import {
    setCheckoutState,
    setClientId,
    setClientName,
} from "../../reducers/checkout";

interface FormValues {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    password: string;
}

const CheckoutUserCreateAccount = () => {
    const { timezone, providerId } = useAppSelector((state: RootState) =>
        state.checkout
    );
    const dispatch = useAppDispatch();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>();
    const [registerClient, { isLoading }] = useRegisterClientMutation();
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
        // Clear any previous errors
        setServerErrors([]);
        try {
            // map form to my request
            const request: RegisterClientRequest = {
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                phone: data.phone,
                email: data.email.trim(),
                password: data.password.trim(),
                timezone: timezone,
                providerId: providerId,
            };
            // Assuming providerId and timezone are optional and not provided in this form
            const response = await registerClient(request).unwrap();
            //console.log("Registration successful:", response);
            if (response.isSuccess) {
                dispatch(setCheckoutState("address"));
                dispatch(setClientId(response.clientId));
                dispatch(
                    setClientName(`${request.firstName} ${request.lastName}`),
                );
            } else {
                setServerErrors(response.errors);
            }
            // Optionally handle success (e.g., navigation or success message)
        } catch (error: any) {
            // Attempt to extract error messages from the response; adjust according to your error shape
            if (error?.data?.errors && Array.isArray(error.data.errors)) {
                setServerErrors(error.data.errors);
            } else {
                setServerErrors([
                    "Registration failed. Please try again later.",
                ]);
            }
            console.error("Registration failed:", error);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <h3 className="mb-3">Create Client Account</h3>
            {serverErrors.length > 0 && (
                <div className="alert alert-danger">
                    <ul className="mb-0">
                        {serverErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Name*</label>
                <div className="col-sm mb-2 mb-md-0">
                    <input
                        type="text"
                        className="form-control"
                        id="inputFirstName"
                        placeholder="First name"
                        autoComplete="given-name"
                        {...register("firstName", {
                            required: "First name is required",
                        })}
                    />
                    {errors.firstName && (
                        <span className="text-danger form-text">
                            {errors.firstName.message}
                        </span>
                    )}
                </div>
                <div className="col-sm">
                    <input
                        type="text"
                        className="form-control"
                        id="inputLastName"
                        placeholder="Last name"
                        autoComplete="family-name"
                        {...register("lastName", {
                            required: "Last name is required",
                        })}
                    />
                    {errors.lastName && (
                        <span className="text-danger form-text">
                            {errors.lastName.message}
                        </span>
                    )}
                </div>
            </div>
            <div className="row mb-3">
                <label htmlFor="inputPhone" className="col-sm-3 col-form-label">
                    Phone*
                </label>
                <div className="col-sm-9">
                    <input
                        type="tel"
                        className="form-control"
                        id="inputPhone"
                        autoComplete="tel"
                        {...register("phone", {
                            required: "Phone is required",
                        })}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <label htmlFor="inputEmail" className="col-sm-3 col-form-label">
                    Email*
                </label>
                <div className="col-sm-9">
                    <input
                        type="email"
                        className="form-control"
                        id="inputEmail"
                        autoComplete="email"
                        {...register("email", {
                            required: "Email is required",
                            pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: "Please enter a valid email address",
                            },
                        })}
                    />
                    {errors.email && (
                        <span className="text-danger form-text">
                            {errors.email.message}
                        </span>
                    )}
                    <div className="form-text">
                        You’ll use your email to login to your account.
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <label
                    htmlFor="inputPassword"
                    className="col-sm-3 col-form-label"
                >
                    Password*
                </label>
                <div className="col-sm-9">
                    <input
                        type="password"
                        className="form-control"
                        id="inputPassword"
                        autoComplete="new-password"
                        {...register("password", {
                            required: "Password is required",
                            minLength: {
                                value: 6,
                                message:
                                    "Password must be at least 6 characters long",
                            },
                            pattern: {
                                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                                message:
                                    "Password must contain a number, an uppercase and a lowercase letter",
                            },
                        })}
                    />
                    {errors.password && (
                        <span className="text-danger form-text">
                            {errors.password.message}
                        </span>
                    )}
                    <div className="form-text">
                        Your password must be at least 6 characters long. It
                        should contain a number, upper and lower case letters.
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-sm-9 offset-sm-3">
                    <input
                        type="submit"
                        className="btn btn-primary"
                        disabled={isLoading}
                        value={isLoading ? "Submitting..." : "Create Account"}
                    />
                </div>
            </div>
        </form>
    );
};

export default CheckoutUserCreateAccount;
